import type { EducationCertificateTemplateApi } from "../services/certificates.api";
import i18n from "@/utils/i18n";

export interface TextPosition {
  x: number; // percentage from left (0-100)
  y: number; // percentage from top (0-100)
  fontSize: number; // in pixels
  fontFamily: string;
  fontWeight: string;
  color: string;
  textAlign: "left" | "center" | "right";
  maxWidth?: number; // percentage of container width
}

export interface CertificateTemplate {
  id: string;
  name: string;
  imagePath: string;
  textPositions: {
    participantName: TextPosition;
    educationTitle: TextPosition;
    instructorName: TextPosition;
    issueDate: TextPosition;
    certificateNumber: TextPosition;
    description: TextPosition;
  };
}

// Default text positions optimized for the certificate template
// Positions are percentages - designed to avoid overlapping
const defaultTextPositions = {
  // Education title at the top
  educationTitle: {
    x: 50,
    y: 22,
    fontSize: 18,
    fontFamily: "'Times New Roman', Georgia, serif",
    fontWeight: "600",
    color: "#1a365d",
    textAlign: "center" as const,
    maxWidth: 70,
  },
  // Participant name - prominent in center
  participantName: {
    x: 50,
    y: 42,
    fontSize: 32,
    fontFamily: "'Times New Roman', Georgia, serif",
    fontWeight: "bold",
    color: "#1a365d",
    textAlign: "center" as const,
    maxWidth: 70,
  },
  // Description below participant name
  description: {
    x: 50,
    y: 55,
    fontSize: 12,
    fontFamily: "'Times New Roman', Georgia, serif",
    fontWeight: "normal",
    color: "#4a5568",
    textAlign: "center" as const,
    maxWidth: 60,
  },
  // Instructor name - bottom left (moved right to avoid corner decoration)
  instructorName: {
    x: 28,
    y: 75,
    fontSize: 14,
    fontFamily: "'Times New Roman', Georgia, serif",
    fontWeight: "500",
    color: "#1a365d",
    textAlign: "center" as const,
    maxWidth: 20,
  },
  // Issue date - bottom right (moved left to avoid corner decoration)
  issueDate: {
    x: 72,
    y: 75,
    fontSize: 14,
    fontFamily: "'Times New Roman', Georgia, serif",
    fontWeight: "500",
    color: "#1a365d",
    textAlign: "center" as const,
    maxWidth: 20,
  },
  // Certificate number - bottom center (same vertical level as instructor and date)
  certificateNumber: {
    x: 50,
    y: 75,
    fontSize: 14,
    fontFamily: "'Times New Roman', Georgia, serif",
    fontWeight: "500",
    color: "#1a365d",
    textAlign: "center" as const,
    maxWidth: 25,
  },
};

export const CERTIFICATE_TEMPLATES: CertificateTemplate[] = [
  {
    id: "classic",
    name: "Klasik",
    imagePath: "/src/assets/certificate-templates/template-classic.jpg",
    textPositions: { ...defaultTextPositions },
  },
  {
    id: "modern",
    name: "Modern",
    imagePath: "/src/assets/certificate-templates/template-modern.webp",
    textPositions: {
      educationTitle: {
        ...defaultTextPositions.educationTitle,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        fontWeight: "300",
        color: "#2c3e50",
      },
      participantName: {
        ...defaultTextPositions.participantName,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        fontWeight: "600",
        color: "#2c3e50",
      },
      description: {
        ...defaultTextPositions.description,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#7f8c8d",
      },
      instructorName: {
        ...defaultTextPositions.instructorName,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#2c3e50",
      },
      issueDate: {
        ...defaultTextPositions.issueDate,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#2c3e50",
      },
      certificateNumber: {
        ...defaultTextPositions.certificateNumber,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#95a5a6",
      },
    },
  },
  {
    id: "corporate",
    name: "Kurumsal",
    imagePath: "/src/assets/certificate-templates/template-classic.jpg",
    textPositions: {
      educationTitle: {
        ...defaultTextPositions.educationTitle,
        fontFamily: "'Georgia', serif",
        color: "#1a365d",
      },
      participantName: {
        ...defaultTextPositions.participantName,
        fontFamily: "'Georgia', serif",
        color: "#1a365d",
      },
      description: {
        ...defaultTextPositions.description,
        fontFamily: "'Georgia', serif",
        color: "#2d3748",
      },
      instructorName: {
        ...defaultTextPositions.instructorName,
        fontFamily: "'Georgia', serif",
        color: "#1a365d",
      },
      issueDate: {
        ...defaultTextPositions.issueDate,
        fontFamily: "'Georgia', serif",
        color: "#1a365d",
      },
      certificateNumber: {
        ...defaultTextPositions.certificateNumber,
        color: "#718096",
      },
    },
  },
  {
    id: "elegant",
    name: "Zarif",
    imagePath: "/src/assets/certificate-templates/template-classic.jpg",
    textPositions: {
      educationTitle: {
        ...defaultTextPositions.educationTitle,
        fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
        color: "#2c2c2c",
      },
      participantName: {
        ...defaultTextPositions.participantName,
        fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
        fontWeight: "normal",
        fontSize: 30,
        color: "#2c2c2c",
      },
      description: {
        ...defaultTextPositions.description,
        fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
        color: "#5a5a5a",
      },
      instructorName: {
        ...defaultTextPositions.instructorName,
        fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
        color: "#2c2c2c",
      },
      issueDate: {
        ...defaultTextPositions.issueDate,
        fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
        color: "#2c2c2c",
      },
      certificateNumber: {
        ...defaultTextPositions.certificateNumber,
        color: "#888888",
      },
    },
  },
  {
    id: "achievement",
    name: i18n.t("certificateTemplates.achievement"),
    imagePath: "/src/assets/certificate-templates/template-classic.jpg",
    textPositions: {
      educationTitle: {
        ...defaultTextPositions.educationTitle,
        fontFamily: "'Cambria', Georgia, serif",
        color: "#8b4513",
      },
      participantName: {
        ...defaultTextPositions.participantName,
        fontFamily: "'Cambria', Georgia, serif",
        color: "#8b4513",
      },
      description: {
        ...defaultTextPositions.description,
        fontFamily: "'Cambria', Georgia, serif",
        color: "#a0522d",
      },
      instructorName: {
        ...defaultTextPositions.instructorName,
        fontFamily: "'Cambria', Georgia, serif",
        color: "#6b4423",
      },
      issueDate: {
        ...defaultTextPositions.issueDate,
        fontFamily: "'Cambria', Georgia, serif",
        color: "#6b4423",
      },
      certificateNumber: {
        ...defaultTextPositions.certificateNumber,
        color: "#8b7355",
      },
    },
  },
  {
    id: "training",
    name: i18n.t("certificateTemplates.education"),
    imagePath: "/src/assets/certificate-templates/template-classic.jpg",
    textPositions: {
      educationTitle: {
        ...defaultTextPositions.educationTitle,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontWeight: "400",
        color: "#2c5282",
      },
      participantName: {
        ...defaultTextPositions.participantName,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontWeight: "600",
        color: "#1e3a5f",
      },
      description: {
        ...defaultTextPositions.description,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#4a5568",
      },
      instructorName: {
        ...defaultTextPositions.instructorName,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#1e3a5f",
      },
      issueDate: {
        ...defaultTextPositions.issueDate,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#1e3a5f",
      },
      certificateNumber: {
        ...defaultTextPositions.certificateNumber,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: "#718096",
      },
    },
  },
];

export const getTemplateById = (id: string): CertificateTemplate | undefined => {
  return CERTIFICATE_TEMPLATES.find((template) => template.id === id);
};

/**
 * Backend'den gelen şablon item'ını CertificateTemplate'e çevirir.
 * fileDownloadUrl → imagePath (görsel URL), varsayılan textPositions kullanılır.
 */
export function mapApiTemplateToCertificateTemplate(
  api: EducationCertificateTemplateApi,
  index: number
): CertificateTemplate {
  return {
    id: api.id ?? `template-${index}`,
    name: api.name ?? `Şablon ${index + 1}`,
    imagePath: api.fileDownloadUrl,
    textPositions: { ...defaultTextPositions },
  };
}
